/**
 * Logo ingest (FR-SET-03a).
 *
 * What a user has to hand is rarely a transparent PNG. The two supplied assets are
 * JPEGs: a wordmark on an off-white textured background, and a 3:4 portrait photograph.
 * Neither drops into a PDF header as-is.
 *
 * Pure: operates on raw RGBA bytes and dimensions. No canvas, no DOM (AD-11) — which is
 * what makes the awkward parts, the flood fill and the crop geometry, testable.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// Background removal
// ---------------------------------------------------------------------------

/** Squared RGB distance. Squared to avoid a sqrt in the inner loop. */
function distanceSq(
  a: number, b: number, c: number,
  r: number, g: number, bl: number,
): number {
  return (a - r) ** 2 + (b - g) ** 2 + (c - bl) ** 2;
}

/**
 * Sample the background colour from the four corners.
 *
 * Assuming pure white would be wrong here: the Fusion Solutions wordmark sits on an
 * off-white *textured* background, so a hard `#ffffff` test removes almost nothing and
 * the logo still prints in a grey box.
 */
export function sampleBackgroundColour(
  data: Uint8ClampedArray,
  { width, height }: Size,
): [number, number, number] {
  const corners = [
    0,
    (width - 1) * 4,
    (height - 1) * width * 4,
    ((height - 1) * width + width - 1) * 4,
  ];
  let r = 0;
  let g = 0;
  let b = 0;
  for (const i of corners) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
  }
  return [Math.round(r / 4), Math.round(g / 4), Math.round(b / 4)];
}

export interface RemoveBackgroundOptions {
  /** 0–255 per-channel tolerance. Higher removes more. */
  tolerance?: number;
  colour?: [number, number, number];
}

/**
 * Make the background transparent, flood-filling inward from the edges.
 *
 * A plain threshold over every pixel would be simpler, but it punches holes anywhere the
 * logo itself contains a light area — a white highlight, a pale fill — because those
 * pixels match the background colour too. Filling only from the border removes what is
 * genuinely *around* the mark and leaves enclosed regions intact.
 *
 * Returns the number of pixels cleared, so a caller can tell "removed the background"
 * from "removed nothing" and report it.
 */
export function removeBackground(
  data: Uint8ClampedArray,
  size: Size,
  { tolerance = 32, colour }: RemoveBackgroundOptions = {},
): number {
  const { width, height } = size;
  const [br, bg, bb] = colour ?? sampleBackgroundColour(data, size);
  const limit = tolerance * tolerance * 3;

  const seen = new Uint8Array(width * height);
  const queue: number[] = [];

  const consider = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return;
    const p = y * width + x;
    if (seen[p]) return;
    seen[p] = 1;
    const i = p * 4;
    if (distanceSq(data[i], data[i + 1], data[i + 2], br, bg, bb) <= limit) queue.push(p);
  };

  for (let x = 0; x < width; x += 1) {
    consider(x, 0);
    consider(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    consider(0, y);
    consider(width - 1, y);
  }

  let cleared = 0;
  while (queue.length > 0) {
    const p = queue.pop() as number;
    data[p * 4 + 3] = 0;
    cleared += 1;
    const x = p % width;
    const y = (p - x) / width;
    consider(x - 1, y);
    consider(x + 1, y);
    consider(x, y - 1);
    consider(x, y + 1);
  }

  return cleared;
}

// ---------------------------------------------------------------------------
// Cropping and fitting
// ---------------------------------------------------------------------------

/**
 * The largest square crop, positioned around a point of interest.
 *
 * `focusY` is a fraction of height. The default of 0.38 is deliberately above centre:
 * on a portrait photograph the face sits in the upper third, and a centre crop of a 3:4
 * portrait tends to land on the chin and shoulders.
 */
export function squareCropRect(
  { width, height }: Size,
  focusY = 0.38,
  focusX = 0.5,
): Rect {
  const side = Math.min(width, height);
  const clamp = (value: number, max: number) => Math.max(0, Math.min(value, max));
  return {
    x: Math.round(clamp(focusX * width - side / 2, width - side)),
    y: Math.round(clamp(focusY * height - side / 2, height - side)),
    width: side,
    height: side,
  };
}

/** Scale to fit inside `max` on the longest edge, never scaling up. */
export function fitWithin({ width, height }: Size, max: number): Size {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const scale = max / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Tighten to the non-transparent content, with padding.
 *
 * After background removal a wordmark usually floats in a wide transparent margin. Left
 * in, that margin is what actually gets scaled into the PDF header, so the mark itself
 * ends up far smaller than the space allotted to it.
 */
export function contentBounds(
  data: Uint8ClampedArray,
  { width, height }: Size,
  padding = 0,
): Rect {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // Fully transparent: report the whole image rather than an inverted rect.
  if (maxX < 0) return { x: 0, y: 0, width, height };

  const x = Math.max(0, minX - padding);
  const y = Math.max(0, minY - padding);
  return {
    x,
    y,
    width: Math.min(width, maxX + 1 + padding) - x,
    height: Math.min(height, maxY + 1 + padding) - y,
  };
}

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export type LogoKind = 'personal' | 'business';

export interface LogoPlan {
  /** Square-crop first — a portrait cannot fill a landscape header slot. */
  square: boolean;
  /** Flood-fill the background to transparency. */
  stripBackground: boolean;
  /** Longest edge of the stored PNG. */
  maxEdge: number;
}

/**
 * How each kind of logo is processed.
 *
 * Personal is a photograph: crop it square so it reads as an avatar, and leave the
 * pixels alone — background removal on a photo produces ragged edges, not a cutout.
 * Business is a mark: keep its aspect ratio, strip the background it was saved on.
 */
export function logoPlan(kind: LogoKind): LogoPlan {
  return kind === 'personal'
    ? { square: true, stripBackground: false, maxEdge: 512 }
    : { square: false, stripBackground: true, maxEdge: 1024 };
}
